/** Factor del Excel Couriers_Control: peso volumétrico (kg) = volumen (m³) × 167. */
export const VOLUMETRIC_KG_PER_M3 = 167;

function roundTo(value, digits) {
  if (!Number.isFinite(value)) return 0;
  const factor = 10 ** digits;
  return Math.round((value + Number.EPSILON) * factor) / factor;
}

export function toNumber(value) {
  if (value === "" || value == null) return null;
  const raw =
    typeof value === "number" ? value : Number(String(value).replace(",", "."));
  return Number.isFinite(raw) ? raw : null;
}

export function computeCourierMeasures({
  largoCm,
  anchoCm,
  altoCm,
  pesoBrutoKg,
  coste,
  costeReal,
} = {}) {
  const largo = toNumber(largoCm) ?? 0;
  const ancho = toNumber(anchoCm) ?? 0;
  const alto = toNumber(altoCm) ?? 0;
  const volumenM3 = roundTo((largo * ancho * alto) / 1_000_000, 6);
  const pesoVolumetricoKg = roundTo(volumenM3 * VOLUMETRIC_KG_PER_M3, 6);
  const pesoBruto = toNumber(pesoBrutoKg) ?? 0;
  const pesoFacturableKg = roundTo(Math.max(pesoBruto, pesoVolumetricoKg), 6);
  const costeN = toNumber(coste);
  const realN = toNumber(costeReal);
  const diferencia =
    costeN == null || realN == null ? null : roundTo(realN - costeN, 4);

  return {
    volumenM3,
    pesoVolumetricoKg,
    pesoBrutoKg: roundTo(pesoBruto, 6),
    pesoFacturableKg,
    coste: costeN == null ? null : roundTo(costeN, 4),
    costeReal: realN == null ? null : roundTo(realN, 4),
    diferencia,
  };
}

/** Etiqueta de una línea: código muestra o nombre libre (Estilo). */
export function lineContentLabel(linea = {}) {
  const codigo = String(linea.CodigoMuestra || "").trim();
  if (codigo) return codigo;
  const estilo = String(linea.Estilo || "").trim();
  if (estilo) return estilo;
  const motivo = String(linea.Motivo || "").trim();
  if (motivo && motivo !== "Movimiento de muestra") return motivo;
  return "";
}

function isPositiveMeasure(value) {
  const n = toNumber(value);
  return n != null && n > 0;
}

/**
 * Campos obligatorios para marcar Enviado (EN_CURSO):
 * nº courier + largo/ancho/alto + peso bruto.
 * Devuelve claves i18n relativas a courierControl.*
 */
export function shippedRequiredMissing(envio = {}) {
  const missing = [];
  if (!String(envio.NumeroAwb || "").trim()) missing.push("courierNumber");
  if (!isPositiveMeasure(envio.LargoCm)) missing.push("length");
  if (!isPositiveMeasure(envio.AnchoCm)) missing.push("width");
  if (!isPositiveMeasure(envio.AltoCm)) missing.push("height");
  if (!isPositiveMeasure(envio.PesoBrutoKg)) missing.push("grossWeight");
  return missing;
}

export function canMarkCourierShipped(envio = {}) {
  return shippedRequiredMissing(envio).length === 0;
}

/** Estados en los que nadie puede tocar piezas (Motivo, código, añadir…). */
export const COURIER_PIECES_LOCKED_ESTADOS = [
  "PREPARANDO_ADUANAS",
  "EN_CURSO",
  "ENTREGADO",
  "CANCELADO",
];

export function isCourierPiecesLocked(estado) {
  const e = String(estado || "PEDIDO").toUpperCase();
  return COURIER_PIECES_LOCKED_ESTADOS.includes(e);
}

/** Bloqueo total para quien solo Solicita (igual que piezas + cabecera). */
export function isCourierLockedForSolicitar(estado) {
  return isCourierPiecesLocked(estado);
}

/** Envío con España (país, destino SDS/Sitges, etc.) — estado Preparando aduanas. */
export function isSpainCourierEnvio(envio = {}) {
  const blob = [envio.Pais, envio.Destino, envio.Origen, envio.Contacto]
    .map((x) => String(x || "").toLowerCase())
    .join(" ");
  if (!blob.trim()) return false;
  return (
    blob.includes("spain") ||
    blob.includes("españa") ||
    blob.includes("espana") ||
    blob.includes("sitges") ||
    /\bsds\b/.test(blob) ||
    blob.includes("sds spain")
  );
}

/**
 * Resumen para el listado: primeras N piezas + "+k" si hay más.
 */
export function summarizeCourierContents(lineas = [], { max = 3 } = {}) {
  const labels = [];
  const seen = new Set();
  for (const linea of lineas || []) {
    const label = lineContentLabel(linea);
    if (!label) continue;
    const key = label.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    labels.push(label);
  }
  const shown = labels.slice(0, Math.max(1, max));
  const extra = Math.max(0, labels.length - shown.length);
  const texto =
    shown.length === 0
      ? ""
      : extra > 0
        ? `${shown.join(", ")} +${extra}`
        : shown.join(", ");
  return { labels, texto, total: labels.length };
}
